import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Table } from 'antd';
import { colorConstants } from 'config/colorConstants';
import tw from 'twin.macro';
import {
    getInputPriceSimulatorColumns,
    getOutputPriceSimulatorColumns,
    getRecommdationPriceSimulatorColumns,
} from 'utils/getPriceSimulatorColumns';
import { getConfig, getPercentageChangeV2, reverseObjectKeyValues, scaleMaxValue } from 'utils/helperMethods';
import KPICardsComponent from '../../KPICardsComponent/KPICardsComponent';
import { Eye, EyeOff, NegativeTriangle, Neutral, PositiveTriangle } from 'assets/svg';
import { priceSimulatorDataContext } from 'contexts/PriceSimulatorContext';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';
import _ from 'lodash';
import SelectionDropDown from 'components/molecules/SelectionDropDown';
import { ConfigKeys } from 'utils/Enums';
import LinkScenarioModal from '../../Modals/LinkScenarioModal';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import {
    getCombinedTableData,
    priceSimulatorScenarioInputTable,
    priceSimulatorScenarioOutputTable,
} from 'parser/graphToExcelDownloadParser';
import Loader from 'components/molecules/Loader';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PSR_IDEN_OPPINPUT_KPI_OPTIONS_DROPDOWN_CHANGED,
    PSR_IDEN_OPPINPUT_TABLE_COMP_FOLLOW_CHANGED,
    PSR_IDEN_OPPINPUT_TABLE_COPY_EVENT_CLICKED,
    PSR_IDEN_OPPINPUT_TABLE_GLOBAL_COMP_FOLLOW_CHANGED,
    PSR_IDEN_OPPINPUT_TABLE_GLOBAL_INPUT_CHANGED,
    PSR_IDEN_OPPINPUT_TABLE_INPUT_PERCENTAGE_CHANGED,
    PSR_IDEN_OPPINPUT_TABLE_INPUT_VALUE_CHANGED,
    PSR_IDEN_OPPLEVEL1_FILTER_CHANGED,
    PSR_IDEN_OPPLEVEL2_FILTER_CHANGED,
} from 'utils/mixpanelEventNames';

const IdentifyOpportunitiesStep = () => {
    const { appConfig } = useContext(UserContext);
    const {
        viewMode,
        levels,
        simulationInputLoading,
        simulationInputDataState,
        setSimulationInputDataState,
        simulationOutputDataState,
        simulationOutputLoading,
        simulationRecommendationDataState,
        simulationRecomLoading,
        title,
        changedPortfolioUpdateKpisLoading,
    } = useContext(priceSimulatorDataContext);
    const { allDimAndAttributeTypeList, idForRestOfComponentLoading } = useContext(FilterContextData);

    const {
        opportunityIdentificationTiles: tiles,
        cascadingColumns,
        inputKeyMapping,
        recommendationKeyMapping,
        outputKeyMapping,
    } = getConfig(ConfigKeys.PRICE_SIMULATOR, appConfig);
    const [level1Index, setLevel1Index] = useState<any>(null);
    const [level2Index, setLevel2Index] = useState<any>(null);
    const [linkTriggeredPPG, setLinkTriggeredPPG] = useState({ label: '', value: -1 });
    const [isLinkModalVisible, setIsLinkModalVisible] = useState(false);
    const [linkPPGState, setLinkPPGState] = useState({ sourceState: -1, destinationPPGs: [] });
    const [showInput, setShotInput] = useState(1);
    const [showOutput, setShowOutput] = useState(1);
    const [showRecommedation, setShowRecommendation] = useState(1);
    const labelTableRef = useRef<any>(null);
    const [calculatedWidth, setCalculatedWidth] = useState(400);
    const [selectedInputKpiOptions, setSelectedInputKpiOptions] = useState<any[]>([]);
    const [inputKpiOptions, setInputKpiOptions] = useState<any[]>(initiliseInputKpisForMultiSelect);
    const [level1ListOptions, setLevel1ListOptions] = useState<any[]>([]);
    const [level2ListOptions, setLevel2ListOptions] = useState<any[]>([]);
    const [filteredLevel1List, setFilteredLevel1List] = useState<any[]>([]);
    const [filteredLevel2List, setFilteredLevel2List] = useState<any[]>([]);

    // toggle columns for recommendation
    const [toggledColumns, setToggledColumns] = useState<any>({
        Strategy: false,
        Mix: false,
        Growth: false,
        Gap: false,
    });

    useEffect(() => {
        if (labelTableRef.current) {
            const mainRec = document?.getElementById('simulation-table-container')?.getBoundingClientRect();
            const totalWidth: any = (mainRec?.width || 0) - 205;
            setCalculatedWidth(totalWidth / openTabsCount());
        }
    }, [labelTableRef?.current, showInput, showOutput, showRecommedation]);

    useEffect(() => {
        // destination elements
        const tableScrollableElement = document?.getElementsByClassName('ant-table-body') || [];
        // source element
        const labelTable = document.getElementById(`scenario-kpi-table`)?.getElementsByClassName('ant-table-body')[0];

        // keep all the scrollable elements in a single array
        const targetElements = [labelTable];
        Array.from(tableScrollableElement).forEach((table: any) => {
            targetElements.push(table);
        });

        targetElements.forEach((element: any, index) => {
            element?.addEventListener('scroll', function () {
                // Update the scroll position of all other scrollable elements
                targetElements.forEach((el: any, i) => {
                    if (i !== index) {
                        el.scrollTop = element.scrollTop;
                    }
                });
            });
        });

        return () => {
            // TODO : add clean up functions
        };
    }, [
        simulationInputLoading,
        simulationOutputLoading,
        simulationRecomLoading,
        simulationOutputDataState,
        simulationInputDataState,
        showInput,
        showOutput,
        showRecommedation,
    ]);

    useEffect(() => {
        if (linkPPGState?.destinationPPGs?.length)
            linkPPGtoPPGMapping(linkPPGState.sourceState, linkPPGState.destinationPPGs);
    }, [linkPPGState]);

    useEffect(() => {
        const simLevel1List = simulationRecommendationDataState
            ?.filter((sim: any) => sim.level === levels[0])
            .map((simulation: any, index: number) => {
                return {
                    label: simulation.label,
                    value: simulation.index,
                };
            });
        setFilteredLevel1List(simLevel1List.map((item) => item.value));
        setLevel1ListOptions(simLevel1List);

        const uniqueRetailers: any[] = [];
        const seenRetailers = new Set();

        simulationRecommendationDataState
            ?.filter((sim: any) => sim.level === levels[1])
            .forEach((i) => {
                if (!seenRetailers.has(i.label)) {
                    seenRetailers.add(i.label);
                    uniqueRetailers.push(i);
                }
            });

        const simLevel2List = Array.from(uniqueRetailers).map((simulation: any, index: number) => {
            return {
                label: simulation.label,
                value: simulation.index,
            };
        });
        setFilteredLevel2List(simLevel2List.map((item) => item.value));
        setLevel2ListOptions(simLevel2List);
    }, [simulationRecommendationDataState]);

    const filteredlevel1Labels = useMemo(() => {
        return level1ListOptions.filter((item) => filteredLevel1List.includes(item.value)).map((i) => i.label);
    }, [filteredLevel1List]);

    const filteredlevel2Labels = useMemo(() => {
        return level2ListOptions.filter((item) => filteredLevel2List.includes(item.value)).map((i) => i.label);
    }, [filteredLevel2List]);

    const selectedInputKpiOptionsWithLabels = useMemo(
        () => inputKpiOptions.filter((item) => selectedInputKpiOptions.includes(item.value)),
        [selectedInputKpiOptions],
    );

    const onCompFollowClick = (isChecked: boolean, rowIndex: number, columnName: string, isGlobal: boolean) => {
        if (isGlobal) {
            trackEventMixpanel(PSR_IDEN_OPPINPUT_TABLE_GLOBAL_COMP_FOLLOW_CHANGED, {
                checked: isChecked,
            });
        } else {
            trackEventMixpanel(PSR_IDEN_OPPINPUT_TABLE_COMP_FOLLOW_CHANGED, {
                checked: isChecked,
            });
        }
        const newData = [...simulationInputDataState];
        if (isGlobal) {
            newData.forEach((item, index) => {
                item[columnName].nested.cp.val = isChecked;
            });
        } else {
            const updatableIndex: any[] = [rowIndex];
            const secondLevel: Array<any> = [];

            simulationInputDataState.forEach((item: any) => {
                if (item.nestedIndex === rowIndex) {
                    updatableIndex.push(item.index);
                    secondLevel.push(item.index);
                }
            });

            simulationInputDataState.forEach((item: any) => {
                if (secondLevel.includes(item.nestedIndex)) {
                    updatableIndex.push(item.index);
                    // secondLevel.push(item.index); // third level indexes should be included
                }
            });

            newData.forEach((item, index) => {
                if (updatableIndex.includes(index)) {
                    item[columnName].nested.cp.val = isChecked;
                }
            });
        }

        setSimulationInputDataState(newData);
    };

    const inputMemoisedColumns = useMemo(() => {
        if (simulationInputDataState?.length > 0)
            return getInputPriceSimulatorColumns(
                false,
                viewMode,
                levels,
                level1Index,
                setLevel1Index,
                level2Index,
                setLevel2Index,
                '',
                inputChangeHandler,
                globalPercentageChangeHandler,
                triggerCopyOrLinkFunctionality,
                onCompFollowClick,
                selectedInputKpiOptionsWithLabels.sort((a: any, b: any) => a.value - b.value), // we need to maintain the same order
            );
    }, [level1Index, simulationInputDataState, level2Index, selectedInputKpiOptionsWithLabels]);

    const outputMemoisedColumns = useMemo(() => {
        return getOutputPriceSimulatorColumns(
            simulationOutputDataState,
            levels,
            level1Index,
            setLevel1Index,
            level2Index,
            setLevel2Index,
            '',
            outputKeyMapping,
        );
    }, [simulationOutputDataState, level1Index, level2Index]);

    const recommendationMemoisedColumns = useMemo(() => {
        return getRecommdationPriceSimulatorColumns(
            levels,
            level1Index,
            setLevel1Index,
            level2Index,
            setLevel2Index,
            '',
            toggledColumns,
            toggleClickHandler,
        );
    }, [
        simulationRecommendationDataState,
        level1Index,
        level2Index,
        toggledColumns,
        filteredLevel1List,
        filteredLevel2List,
    ]);

    function inputChangeHandler(
        value: any,
        rowData: any,
        columnName: any,
        isNewPriceChange: boolean,
        roundingDecimal: number,
    ) {
        if (!['+', '-'].includes(value) && isNaN(value)) return;

        if (isNewPriceChange) {
            trackEventMixpanel(PSR_IDEN_OPPINPUT_TABLE_INPUT_VALUE_CHANGED, {
                columanName: columnName,
                value: value,
            });
        } else {
            trackEventMixpanel(PSR_IDEN_OPPINPUT_TABLE_INPUT_PERCENTAGE_CHANGED, {
                columanName: columnName,
                value: value,
            });
        }

        const updatableIndex = [rowData.index];

        const secondLevel: Array<any> = [];

        simulationInputDataState.forEach((item: any) => {
            if (item.nestedIndex === rowData.index) {
                updatableIndex.push(item.index);
                secondLevel.push(item.index);
            }
        });

        simulationInputDataState.forEach((item: any) => {
            if (secondLevel.includes(item.nestedIndex)) {
                updatableIndex.push(item.index);
                // secondLevel.push(item.index); // third level indexes should be included
            }
        });

        const newData = _.cloneDeep(simulationInputDataState);

        newData.forEach((item, index) => {
            if (updatableIndex.includes(index)) {
                item[columnName].nested.np.val = isNewPriceChange
                    ? value
                    : value === ''
                    ? ''
                    : _.round(
                          scaleMaxValue(!['+', '-'].includes(value) ? value : 1, item[columnName].nested.cp.val),
                          roundingDecimal,
                      );
                item[columnName].nested.pc.val = !isNewPriceChange
                    ? value
                    : value === ''
                    ? ''
                    : getPercentageChangeV2(item[columnName].nested.cp.val, value);
            }
        });

        // if its cascading colum, update the dataset accodingly
        if (cascadingColumns.slice(0, 2).includes(columnName) && !isNaN(value)) {
            // if its COGS
            if (columnName === cascadingColumns[0]) {
                cascadeChanges(
                    cascadingColumns[0],
                    cascadingColumns[1],
                    newData,
                    updatableIndex,
                    false,
                    false,
                    roundingDecimal,
                );
            }
            cascadeChanges(
                cascadingColumns[1],
                cascadingColumns[2],
                newData,
                updatableIndex,
                true,
                false,
                roundingDecimal,
            );
            // for list price updates
            cascadeOIChanges(
                cascadingColumns[1],
                ['OI Rate', 'Non OI Rate', 'Promo Trade Rate Spends'],
                newData,
                updatableIndex,
                false,
                false,
                roundingDecimal,
            );
        }

        // update Trade rate if OI, Non OI, promo trends is changed
        if (['OI Rate', 'Non OI Rate', 'Promo Trade Rate Spends'].includes(columnName)) {
            updateTradeRateBasedOiColumnss(newData, updatableIndex, false);
        }

        setSimulationInputDataState(newData);
    }

    const updateTradeRateBasedOiColumnss = (
        dataSet: Array<any>,
        updatableIndexes: Array<number>,
        updateAllIndex: boolean, // for global change handler we will use this prop
    ) => {
        dataSet.forEach((item: any, index) => {
            if (updateAllIndex || updatableIndexes.includes(index)) {
                const updatedOi = item['OI Rate'].nested.np.val || item['OI Rate'].nested.cp.val;
                const updatedNonOi = item['Non OI Rate'].nested.np.val || item['Non OI Rate'].nested.cp.val;
                const updatedPromoTradeSpends =
                    item['Promo Trade Rate Spends'].nested.np.val || item['Promo Trade Rate Spends'].nested.cp.val;
                const updatedListPrice = item['List Price'].nested.np.val || item['List Price'].nested.cp.val;

                const updatedTradeRate =
                    ((Number(updatedOi) + Number(updatedNonOi) + Number(updatedPromoTradeSpends)) * 100) /
                    Number(updatedListPrice);

                // new price change
                item['Trade Rate'].nested.np.val = isFinite(updatedTradeRate) ? _.round(updatedTradeRate, 2) : 0;
                // update percentage change
                item['Trade Rate'].nested.pc.val = getPercentageChangeV2(
                    item['Trade Rate'].nested.cp.val,
                    item['Trade Rate'].nested.np.val,
                );
            }
        });
    };

    const cascadeOIChanges = (
        parentKey: string, // its always list price
        childKeys: string[],
        dataSet: Array<any>,
        updatableIndexes: Array<number>,
        isRetailPrice: boolean,
        updateAllIndex: boolean, // for global change handler we will use this prop
        roundingDecimal: number,
    ) => {
        dataSet.forEach((item: any, index) => {
            if (updateAllIndex || updatableIndexes.includes(index)) {
                childKeys.forEach((childKey) => {
                    let currentChildPercentChange = 1 + Number(item[parentKey].nested.pc.val) / 100;

                    // update current price
                    const newPrice = _.round(item[childKey].nested.cp.val * currentChildPercentChange, roundingDecimal);

                    item[childKey].nested.np.val = isFinite(newPrice) ? newPrice : 0;

                    // update percentage change
                    item[childKey].nested.pc.val = item[parentKey].nested.pc.val;
                });
            }
        });
    };

    const cascadeChanges = (
        parentKey: string,
        childKey: string,
        dataSet: Array<any>,
        updatableIndexes: Array<number>,
        isRetailPrice: boolean,
        updateAllIndex: boolean, // for global change handler we will use this prop
        roundingDecimal: number,
    ) => {
        dataSet.forEach((item: any, index) => {
            if (updateAllIndex || updatableIndexes.includes(index)) {
                // calculate current margin %
                let currentChildPrice = item[childKey].nested.cp.val;
                let currentParentPrice = item[parentKey].nested.cp.val;
                let margin = (currentChildPrice - currentParentPrice) / currentChildPrice;

                // update current price
                const newPrice = isRetailPrice
                    ? // ? _.round(item[parentKey].nested.np.val / (1 - margin), 1) - 0.01
                      _.round(item[parentKey].nested.np.val / (1 - margin), roundingDecimal)
                    : _.round(item[parentKey].nested.np.val / (1 - margin), roundingDecimal);
                item[childKey].nested.np.val = isFinite(newPrice) ? newPrice : 0;
                // update percentage change
                item[childKey].nested.pc.val = getPercentageChangeV2(
                    item[childKey].nested.cp.val,
                    item[childKey].nested.np.val,
                );
            }
        });
    };

    function globalPercentageChangeHandler(value: any, columnName: string, roundingDecimal: number) {
        if (isNaN(value) || !value) {
            return;
        }
        trackEventMixpanel(PSR_IDEN_OPPINPUT_TABLE_GLOBAL_INPUT_CHANGED, {
            columanName: columnName,
            value,
        });
        const newData = [...simulationInputDataState];
        newData.forEach((item, index) => {
            item[columnName].nested.np.val =
                value === '' ? '' : _.round(scaleMaxValue(value, item[columnName].nested.cp.val), roundingDecimal);
            item[columnName].nested.pc.val = value;
        });

        // if its cascading colum, update the dataset accodingly
        if (cascadingColumns.slice(0, 2).includes(columnName)) {
            if (columnName === cascadingColumns[0]) {
                cascadeChanges(cascadingColumns[0], cascadingColumns[1], newData, [], false, true, roundingDecimal);
            }
            cascadeChanges(cascadingColumns[1], cascadingColumns[2], newData, [], true, true, roundingDecimal);

            cascadeOIChanges(
                cascadingColumns[1],
                ['OI Rate', 'Non OI Rate', 'Promo Trade Rate Spends'],
                newData,
                [],
                false,
                true,
                roundingDecimal,
            );
        }

        // update Trade rate if OI, Non OI, promo trends is changed
        if (['OI Rate', 'Non OI Rate', 'Promo Trade Rate Spends'].includes(columnName)) {
            updateTradeRateBasedOiColumnss(newData, [], true);
        }

        setSimulationInputDataState(newData);
    }

    function triggerCopyOrLinkFunctionality(rowdata: any) {
        setLinkTriggeredPPG({ label: rowdata?.label, value: rowdata?.index });
        setIsLinkModalVisible(true);
        trackEventMixpanel(PSR_IDEN_OPPINPUT_TABLE_COPY_EVENT_CLICKED, {});
    }

    const closeLinkingModal = () => {
        setIsLinkModalVisible(false);
    };

    function toggleClickHandler(item: string) {
        const newState = { ...toggledColumns };
        newState[item] = !newState[item];
        setToggledColumns(newState);
    }

    function initiliseInputKpisForMultiSelect() {
        const inputkpiMappingOptions = Object.keys(inputKeyMapping)?.map((kpi: any, index: number) => {
            return {
                label: inputKeyMapping[kpi],
                value: index,
            };
        });
        // set default SelectedValues
        setSelectedInputKpiOptions(inputkpiMappingOptions.map((item) => item.value));

        return inputkpiMappingOptions;
    }

    const linkPPGtoPPGMapping = (sourcePPGIndex: number, destinationPPGSIndexes: number[]) => {
        const updatableIndex: Array<number> = [...destinationPPGSIndexes];

        // add all the indexes where nestedIndex is destinatinoPPGs indexes
        simulationInputDataState.forEach((item: any) => {
            if (destinationPPGSIndexes.includes(item.nestedIndex)) {
                updatableIndex.push(item.index);
            }
        });

        const newData = [...simulationInputDataState];
        // access through all the KPIS and update each value from soucre to destination
        const kpiList = inputKpiOptions.map((item: any) => item.label);
        const sourceRow = newData.find((item: any) => item.index === sourcePPGIndex);

        newData.forEach((newDataItem: any) => {
            if (updatableIndex.includes(newDataItem.index)) {
                kpiList.forEach((col: any) => {
                    if (col === 'Comp Follow') {
                        newDataItem[col].nested.cp.val = sourceRow[col].nested?.cp?.val;
                    } else {
                        const newPriceColValue = sourceRow[col]?.nested?.np?.val || sourceRow[col]?.nested?.np?.rval; // when value is not present consider recommendation value
                        newDataItem[col].nested.np.val = newPriceColValue;
                        newDataItem[col].nested.pc.val = getPercentageChangeV2(
                            newDataItem[col].nested.cp.val,
                            newPriceColValue,
                        );
                    }
                });
            }
        });

        // updating new state
        setSimulationInputDataState(newData);
        // resetting linking ppg
        setLinkPPGState({ sourceState: -1, destinationPPGs: [] });
    };

    const recommendationLoading =
        allDimAndAttributeTypeList?.loading || simulationRecomLoading || idForRestOfComponentLoading;
    const inputLoading =
        allDimAndAttributeTypeList?.loading ||
        simulationInputLoading ||
        idForRestOfComponentLoading ||
        changedPortfolioUpdateKpisLoading;
    const outputLoading = allDimAndAttributeTypeList?.loading || simulationOutputLoading;

    const renderDownloadExcel = () => {
        if (openTabsCount() === 1 && showInput) {
            const inputKPIMapping = reverseObjectKeyValues(inputKeyMapping);

            const headerLabel = [...levels];
            Object.keys(inputKPIMapping).forEach((k: string) => {
                if (k === 'Comp Follow') {
                    headerLabel.push('Comp Follow');
                } else {
                    headerLabel.push('Current ' + k);
                    headerLabel.push('New ' + k);
                    headerLabel.push('Perc Chg');
                }
            });

            return (
                <DownloadExcel
                    title={'ScenarioInputTable'}
                    excelData={priceSimulatorScenarioInputTable(
                        simulationInputDataState,
                        Object.keys(inputKPIMapping),
                        levels,
                        false,
                    )}
                    headers={headerLabel}
                    excludeFilters={true}
                />
            );
        }

        if (openTabsCount() === 1 && showOutput) {
            const headerLabel: string[] = ['Scenario Name', ...levels];
            if (simulationOutputDataState && simulationOutputDataState?.length > 0) {
                Object.keys(simulationOutputDataState[0]).forEach((item: string) => {
                    if (!['label', 'level', 'index', 'nestedIndex'].includes(item)) headerLabel.push(item);
                });
            }
            return (
                <DownloadExcel
                    title={'ScenarioOutputTable'}
                    excelData={priceSimulatorScenarioOutputTable(
                        simulationOutputDataState,
                        headerLabel.slice(3),
                        levels,
                        title,
                    )}
                    headers={headerLabel}
                    excludeFilters={true}
                />
            );
        }
        if (openTabsCount() === 1 && showRecommedation) {
            const headerLabel: string[] = ['Scenario Name', ...levels];

            if (simulationRecommendationDataState) {
                Object.keys(recommendationKeyMapping).forEach((item: string) => {
                    headerLabel.push(recommendationKeyMapping[item].label);
                });
            }
            return (
                <DownloadExcel
                    title={'ScenarioRecommendationTable'}
                    excelData={priceSimulatorScenarioOutputTable(
                        simulationRecommendationDataState,
                        headerLabel.slice(3),
                        levels,
                        title,
                    )}
                    headers={headerLabel}
                    excludeFilters={true}
                />
            );
        }

        // export all at once
        if (openTabsCount() === 3) {
            let finalData = getCombinedTableData(
                levels,
                simulationRecommendationDataState,
                simulationInputDataState,
                simulationOutputDataState,
                inputKeyMapping,
                recommendationKeyMapping,
                title,
            );

            return (
                <DownloadExcel
                    title={'All Tables Data'}
                    excelData={[]}
                    headers={[]}
                    excludeFilters={true}
                    completeDataWithHeaders={finalData}
                />
            );
        }

        return null;
    };

    const openTabsCount = () => {
        return showInput + showOutput + showRecommedation;
    };

    const renderTableTiles = () => {
        return (
            <div className="flex  h-5 gap-1" id="price-sim-table-tiles">
                {showInput === 0 && (
                    <button
                        onClick={() => setShotInput(showInput ? 0 : 1)}
                        className="rounded-t bg-bgBackground px-1 cursor-pointer  border border-borderGrey flex items-center font-xs gap-1 py-1 text-purpleFont"
                    >
                        Input
                        <EyeOff />
                    </button>
                )}
                {showOutput === 0 && (
                    <button
                        onClick={() => setShowOutput(showOutput ? 0 : 1)}
                        className="rounded-t bg-bgBackground px-1 cursor-pointer border border-borderGrey flex items-center font-xs gap-1 py-1 text-purpleFont"
                    >
                        Output
                        <EyeOff />
                    </button>
                )}
                {showRecommedation === 0 && (
                    <button
                        onClick={() => setShowRecommendation(showRecommedation ? 0 : 1)}
                        className="rounded-t bg-bgBackground px-1  cursor-pointer border border-borderGrey flex items-center font-xs gap-1 py-1 text-purpleFont"
                    >
                        Recommendation
                        <EyeOff />
                    </button>
                )}
            </div>
        );
    };

    const renderRecommendationTable = () => {
        return (
            <div
                style={{
                    width: calculatedWidth,
                    position: 'relative',
                }}
                id="price-sim-recommendation-table"
            >
                <button
                    style={{
                        top: '-22px',
                        backgroundColor: '#F7F5FF',
                    }}
                    onClick={() => setShowRecommendation(showRecommedation ? 0 : 1)}
                    className="rounded-t px-4 absolute border border-borderGrey flex gap-2 items-center justify-center"
                >
                    <span className="text-purpleFont font-medium ">Recommendation</span>
                    <Eye />
                </button>
                {simulationRecommendationDataState?.length > 0 && (
                    <CustomTablePriceSimulationOthers
                        bordered
                        style={{
                            opacity: recommendationLoading ? '0.5' : 1,
                            pointerEvents: recommendationLoading ? 'none' : 'auto',
                        }}
                        columns={recommendationMemoisedColumns.slice(1, recommendationMemoisedColumns.length)}
                        pagination={false}
                        scroll={{ x: '100%', y: 400 }}
                        className="mb-2 info_tables"
                        sticky
                        loading={recommendationLoading}
                        dataSource={simulationRecommendationDataState.filter(filterOptionsByLevelFilters)}
                    />
                )}
                {simulationRecommendationDataState?.length === 0 && (
                    <div
                        style={{ height: '551px' }}
                        className="flex items-center justify-center  text-center border border-borderGrey items-center font-xs  text-fontGreyAlt1 font-normal"
                    >
                        {inputLoading ? (
                            <Loader />
                        ) : (
                            <div className="flex flex-col justify-start w-full h-full">
                                <div className="bg-bgBackground h-16 z-10" style={{ height: '138px' }}></div>
                                <div className="px-4 flex  flex-grow items-center justify-center font-xs text-fontGreyAlt1 font-normal ">
                                    No Data
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderInputTable = () => {
        return (
            <div
                style={{
                    width: calculatedWidth,
                    position: 'relative',
                }}
                id="price-sim-input-table"
            >
                <button
                    style={{
                        top: '-22px',
                        backgroundColor: '#F7F5FF',
                    }}
                    onClick={() => setShotInput(showInput ? 0 : 1)}
                    className="rounded-t px-2 absolute border border-borderGrey flex gap-2 items-center justify-center"
                >
                    <span className="text-purpleFont font-medium">Input</span>
                    <Eye />
                </button>
                {simulationInputDataState.length > 0 && (
                    <CustomPriceSimulationInputTable
                        bordered
                        style={{
                            opacity: inputLoading ? '0.5' : 1,
                            pointerEvents: inputLoading ? 'none' : 'auto',
                        }}
                        columns={inputMemoisedColumns}
                        pagination={false}
                        scroll={{ x: '100%', y: 400 }}
                        sticky
                        loading={inputLoading}
                        dataSource={simulationInputDataState.filter(filterOptionsByLevelFilters)}
                        className="mb-2info_tables"
                    />
                )}
                {simulationInputDataState.length === 0 && (
                    <div
                        style={{ height: '551px' }}
                        className="flex items-center justify-center text-center border border-borderGrey items-center font-xs text-fontGreyAlt1 font-normal"
                    >
                        {inputLoading ? (
                            <Loader />
                        ) : (
                            <div className="flex flex-col justify-start w-full h-full">
                                <div className="bg-bgBackground h-16 z-10" style={{ height: '136px' }}></div>
                                <div className="px-4 flex  flex-grow items-center justify-center font-xs text-fontGreyAlt1 font-normal ">
                                    No Data
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderOuputTable = () => {
        return (
            <div
                style={{
                    width: calculatedWidth,
                    position: 'relative',
                }}
                id="price-sim-output-table"
            >
                <button
                    style={{
                        top: '-22px',
                        backgroundColor: '#F7F5FF',
                    }}
                    onClick={() => setShowOutput(showOutput ? 0 : 1)}
                    className="rounded-t px-4 absolute border border-borderGrey flex gap-2 items-center justify-center"
                >
                    <span className="text-purpleFont font-medium">Output</span>
                    <Eye />
                </button>
                {simulationOutputDataState.length > 0 && (
                    <CustomTablePriceSimulationOthers
                        bordered
                        style={{
                            opacity: outputLoading ? '0.5' : 1,
                            pointerEvents: outputLoading ? 'none' : 'auto',
                        }}
                        columns={outputMemoisedColumns?.slice(1, outputMemoisedColumns.length)}
                        pagination={false}
                        scroll={{ x: '100%', y: 400 }}
                        className="mb-2 info_tables"
                        sticky
                        loading={outputLoading}
                        dataSource={simulationOutputDataState.filter(filterOptionsByLevelFilters)}
                    />
                )}
                {simulationOutputDataState.length === 0 && (
                    <div style={{ height: '551px' }} className="flex  rounded-r text-center border border-borderGrey">
                        {outputLoading ? (
                            <Loader />
                        ) : (
                            <div className="flex flex-col justify-start w-full">
                                <div className="bg-bgBackground h-16 z-10" style={{ height: '136px' }}></div>
                                <div className="px-4 flex  flex-grow items-center justify-center font-xs text-fontGreyAlt1 font-normal ">
                                    Enter price values in the "Input" section and click "simulate" to generate output
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    function filterOptionsByLevelFilters(item: any) {
        if (item.level !== levels[0]) return filteredlevel2Labels.includes(item.label);
        return filteredlevel1Labels.includes(item.label);
    }

    return (
        <div id="price-sim-identify-opp-step">
            {/* KPI tiles component */}
            <KPICardsComponent />

            <div className="flex justify-between py-2">
                <div className="flex justify-center items-center" id="price-sim-identify-opp-header">
                    <div className="font-md my-4 font-medium mr-4">Identify Opportunities </div>

                    {showInput === 1 && (
                        <SelectionDropDown
                            list={inputKpiOptions}
                            checkedListObject={''}
                            disabled={false} // make it optional
                            setSelectedList={setSelectedInputKpiOptions}
                            selectedList={selectedInputKpiOptions || []}
                            eventMixPanelString={PSR_IDEN_OPPINPUT_KPI_OPTIONS_DROPDOWN_CHANGED}
                            className="dropdown-width-multi-select"
                            defaultDisplayLabel="Input KPI Options"
                        />
                    )}
                </div>
                <div className="flex gap-4 items-center">
                    <PriceSimulatorLegends />
                    {renderDownloadExcel()}
                </div>
            </div>

            <div id="simulation-table-container">
                {renderTableTiles()}
                {recommendationLoading ? (
                    <Loader />
                ) : (
                    <div className="flex relative  ">
                        <div className="w-52" ref={labelTableRef}>
                            {simulationRecommendationDataState?.length > 0 ? (
                                <CustomTablePriceSimulatorForLabel
                                    bordered
                                    style={{
                                        opacity: recommendationLoading ? '0.5' : 1,
                                        pointerEvents: recommendationLoading ? 'none' : 'auto',
                                    }}
                                    columns={recommendationMemoisedColumns?.slice(0, 1)}
                                    pagination={false}
                                    scroll={{ x: '100%', y: 400 }}
                                    sticky
                                    loading={recommendationLoading}
                                    dataSource={simulationRecommendationDataState.filter(filterOptionsByLevelFilters)}
                                    className="mb-2 rounded-bl"
                                    id={'scenario-kpi-table'}
                                />
                            ) : (
                                <div className="flex flex-col justify-start w-full h-full border  border-borderGrey  ">
                                    <div className="bg-bgBackground h-16 z-10" style={{ height: '138px' }}></div>
                                    <div className="px-4 flex  flex-grow items-center justify-center font-xs text-fontGreyAlt1 font-normal ">
                                        No Data
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* render recommendation table */}
                        {showRecommedation === 1 && renderRecommendationTable()}
                        {/* render input table */}
                        {showInput === 1 && renderInputTable()}
                        {/* render output table */}
                        {showOutput === 1 && renderOuputTable()}
                        {/* render empty info */}
                        {openTabsCount() === 0 && (
                            <div className="flex flex-col flex-grow">
                                <div className="border h-full border-borderGrey h-full text-fontGreyAlt1  rounded flex items-center justify-center">
                                    Click on Tabs to view the Details
                                </div>
                            </div>
                        )}

                        <div className="absolute flex flex-col p-2 z-10">
                            <div className="flex  flex-col" id="price-sim-level1-filter">
                                <span className="text-purpleFont font-medium  font-xs">
                                    Filter by {_.capitalize(levels[0])}
                                </span>
                                <SelectionDropDown
                                    list={level1ListOptions}
                                    checkedListObject={''}
                                    disabled={false}
                                    setSelectedList={setFilteredLevel1List}
                                    selectedList={filteredLevel1List || []}
                                    eventMixPanelString={PSR_IDEN_OPPLEVEL1_FILTER_CHANGED}
                                    className="dropdown-width-multi-select"
                                    defaultDisplayLabel="Input KPI Options"
                                    searchable={true}
                                />
                            </div>
                            <div className="flex  flex-col mt-2" id="price-sim-level2-filter">
                                <span className="text-purpleFont font-medium font-xs">
                                    Filter by {_.capitalize(levels[1])}
                                </span>
                                <SelectionDropDown
                                    list={level2ListOptions}
                                    checkedListObject={''}
                                    disabled={false}
                                    setSelectedList={setFilteredLevel2List}
                                    selectedList={filteredLevel2List || []}
                                    eventMixPanelString={PSR_IDEN_OPPLEVEL2_FILTER_CHANGED}
                                    className="dropdown-width-multi-select"
                                    defaultDisplayLabel="Input KPI Options"
                                    searchable={true}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <LinkScenarioModal
                visible={isLinkModalVisible}
                closeModal={closeLinkingModal}
                linkTriggeredPPG={linkTriggeredPPG}
                setLinkPPGState={setLinkPPGState}
            />
        </div>
    );
};

export default IdentifyOpportunitiesStep;

export const PriceSimulatorLegends = () => {
    return (
        <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
                <PositiveTriangle /> <span>Positive </span>
            </span>
            <span className="flex items-center gap-2">
                <Neutral /> <span>Neutral </span>
            </span>
            <span className="flex items-center gap-2">
                <NegativeTriangle /> <span>Negative </span>
            </span>
        </div>
    );
};

const CustomTablePriceSimulatorForLabel = styled(Table)`
    .ant-table-sticky-scroll {
        position: static;
        display: none;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 136px;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 50px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    .ant-table-cell .empty {
        min-height: 37.69px;
    }

    .label-cell {
        min-height: 37.69px;
        max-height: 37.69px;
    }

    border: ${'1px solid #DBDFE9'};
`;

export const CustomPriceSimulationInputTable = styled(Table)`
    .ant-table-sticky-scroll {
        position: static;
        display: none;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 68px;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 50px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }

    tbody .ant-table-cell input {
        height: 37.69px;
    }
    border: ${'1px solid #DBDFE9'};
`;

const CustomTablePriceSimulationOthers = styled(Table)`
    .ant-table-sticky-scroll {
        position: static;
        display: none;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 136px;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 50px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    .ant-table-cell div {
        height: 37.69px;
    }

    border: ${'1px solid #DBDFE9'};
`;
